// England designations render CLIENT-SIDE from ArcGIS FeatureServers, not as WMS
// tiles (BBB-457). The EA WMS sits behind GeoWebCache, which serves pre-rendered
// tiles and ignores SLD_BODY, so tile colours cannot be changed upstream at any
// price — verified 07/08/2026. Fetching features and drawing them ourselves is
// the only way to meet the colourblind-safe requirement.
//
// Treatment is a distinct colour AND stroke pattern per layer, so overlaps stay
// readable (outlines don't blend into an off-legend colour the way opaque fills
// did) and nothing is distinguished by hue alone. Each layer also carries a faint
// fill so the farmer can tell which side of a boundary is inside — see
// DEFAULT_FILL_OPACITY in the adapter. That fill is what BBB-456 complained about
// when it was opaque; it is not absent, just held down.
//
// minZoom is 10, not 8: a zoom-8 viewport pulls ~4 MB across the five layers,
// versus ~118 KB at zoom 12. Field names differ per service and were read from
// each service's own metadata — they are NOT interchangeable.
export const DESIGNATION_SOURCES = [
  {
    id: 'sssi-england',
    region: 'england',
    kind: 'arcgis-vector',
    serviceUrl:
      'https://services.arcgis.com/JJzESW51TqeY9uat/arcgis/rest/services/SSSI_England/FeatureServer/0',
    nameField: 'NAME',
    outlineColour: '#009E73',
    outlineStyle: 'solid',
    outlineWeight: 3,
    // Hatch direction is the SECOND encoding on top of hue, so a deutan or
    // protan reader has a differentiator that does not depend on telling
    // #009E73 from #0072B2. Directions are the CVD-validated mapping from the
    // 23/07 accessibility spec (shape://slash, backslash, times) and are not
    // free to re-pick: the legend swatches in lib/swatchStyle.js render the same
    // three under the same names since BBB-525, and lib/swatchStyle.js now
    // throws on a direction it does not know rather than substituting one.
    hatchDirection: 'slash',
    pageSize: 1000,
    minZoom: 10,
    licenceStatus: 'verified',
    precedence: 100,
  },
  {
    id: 'sac-england',
    region: 'england',
    kind: 'arcgis-vector',
    serviceUrl:
      'https://services.arcgis.com/JJzESW51TqeY9uat/arcgis/rest/services/Special_Areas_of_Conservation_England/FeatureServer/0',
    nameField: 'SAC_NAME',
    // Reddish purple, not the Okabe-Ito blue #0072B2 it used to be. Davin picked
    // this by eye on 18/08 from a rendered comparison: bluish green against blue
    // was too close to separate at farm zoom over aerial imagery, whatever the
    // validator said about deutan ΔE. Green / purple / orange are maximally
    // separated hues and all three are still Okabe-Ito, so the palette stays
    // colourblind-safe.
    //
    // Yes, this is also the safeguard zones' colour. That used to be a clash and
    // is not any more: safeguard lives in the England-only 'nitrate-water' layer,
    // only one layer is ever visible at a time, and the two carry different
    // patterns anyway.
    outlineColour: '#CC79A7',
    outlineStyle: 'dashed',
    outlineWeight: 3,
    hatchDirection: 'backslash',
    pageSize: 250,
    minZoom: 10,
    licenceStatus: 'verified',
    precedence: 110,
  },
  {
    id: 'spa-england',
    region: 'england',
    kind: 'arcgis-vector',
    serviceUrl:
      'https://services.arcgis.com/JJzESW51TqeY9uat/arcgis/rest/services/Special_Protection_Areas_England/FeatureServer/0',
    nameField: 'SPA_NAME',
    outlineColour: '#E69F00',
    outlineStyle: 'dotted',
    outlineWeight: 3,
    // VERTICAL, changed from cross on 19/08/2026. Davin's call after seeing it
    // rendered, and it removes an ambiguity the cross had created.
    //
    // The cross was two lines corner to corner, which is 45 and -45: exactly
    // SSSI's angle and SAC's angle. So wherever SSSI and SAC overlapped inside
    // this layer, the pair drew both diagonals and read as SPA's mark. Three
    // designations, and one of them was indistinguishable from the other two
    // together. Vertical sits 45 degrees off both, so no combination of the other
    // two can imitate it.
    //
    // The superseded reasoning was that a cross "stays readable through another
    // hatch where a third diagonal would just look like moire". The moire point
    // was sound and vertical is not a third diagonal, so it keeps the benefit
    // without the collision. It also drops SPA from two sets of lines to one,
    // which is most of why it read heaviest of the three.
    //
    // Nothing else in England uses vertical: the NVZ and safeguard pair are WMS
    // in Defra's own styling, not hatched. Scotland uses vertical for SAC in its
    // native scheme, and only one layer is ever visible at a time.
    hatchDirection: 'vertical',
    pageSize: 250,
    // SPA geometry is pathologically detailed — coastal and estuarine boundaries
    // make one unsimplified viewport ~43 MB, against ~390 bytes/feature for SSSI
    // at the same tolerance. It needs a coarser simplification than the others.
    simplifyPixels: 4,
    minZoom: 10,
    licenceStatus: 'verified',
    precedence: 120,
  },
  // Wales: split fused WMS so SPA outline is not occluded by SSSI fill (BBB-457).
  // Draw order follows precedence (higher on top) — SPA last.
  {
    id: 'nrw-sssi-wales',
    region: 'wales',
    kind: 'wms',
    wmsBase: 'https://datamap.gov.wales/geoserver/inspire-nrw/wms',
    layerName: 'inspire-nrw:NRW_SSSI',
    minZoom: 8,
    licenceStatus: 'verified',
    unverified: false,
    precedence: 100,
    legendGroup: 'nrw-designated-sites-wales',
  },
  {
    id: 'nrw-sac-wales',
    region: 'wales',
    kind: 'wms',
    wmsBase: 'https://datamap.gov.wales/geoserver/inspire-nrw/wms',
    layerName: 'inspire-nrw:NRW_SAC',
    minZoom: 8,
    licenceStatus: 'verified',
    unverified: false,
    precedence: 110,
    legendGroup: 'nrw-designated-sites-wales',
  },
  {
    id: 'nrw-spa-wales',
    region: 'wales',
    kind: 'wms',
    wmsBase: 'https://datamap.gov.wales/geoserver/inspire-nrw/wms',
    layerName: 'inspire-nrw:NRW_SPA',
    minZoom: 8,
    licenceStatus: 'verified',
    unverified: false,
    precedence: 120,
    legendGroup: 'nrw-designated-sites-wales',
  },
  {
    id: 'naturescot-designated-scotland',
    region: 'scotland',
    kind: 'wms',
    wmsBase: 'https://ogc.nature.scot/geoserver/protectedareas/wms',
    layerName: 'sssi,sac,spa',
    minZoom: 8,
    licenceStatus: 'verified',
    unverified: false,
    precedence: 100,
  },
];
